<script lang="ts" setup>
import { Heading, type HeadingProps } from "~/components/clarity-ui/Heading";
import DotPattern from "~/templates/elements/DotPattern.vue";
import PictureElement from "~/templates/elements/PictureElement.vue";
import SvgIcon from "~/templates/elements/SvgIcon.vue";
import { DIMENSIONS } from "~/utils/helper/imaging";
import { containsImage } from "~/utils/helper/magnolia";
import { contentToTailwindClasses } from "~/utils/helper/tailwind";
import type { mgnlHeadline } from "~/types/magnolia";

export interface QuoteComponentProps {
  backgroundColor?: string;
  dotPattern?: boolean;
  fontSize?: number;
  headline?: mgnlHeadline;
  image?: object;
  showQuote?: boolean;
  subline?: string;
}

defineProps<QuoteComponentProps>();

const BREAKPOINTS = {
  start: DIMENSIONS["sm"].width["1/2"].ratio["original"],
};

// todo: these mappings are necessary to map the Magnolia datafeed to component's interface
const headingSizeMapping = ["xxlarge", "xlarge", "large", "medium"] as Pick<HeadingProps, "size">;
</script>

<template>
  <div>
    <div
      :class="
        contentToTailwindClasses({
          backgroundColor,
        })
      "
      class="relative rounded-xl py-12 xl:py-16"
    >
      <DotPattern v-if="dotPattern" :variant="dotPattern" />
      <div
        class="relative mx-auto flex w-10/12 flex-col items-center gap-4 md:flex-row md:items-start"
      >
        <div v-if="containsImage(image)" class="shrink-0 grow-0 md:w-5/12 xl:w-3/12">
          <PictureElement
            :breakpoints="BREAKPOINTS"
            :img="image"
            img-class="w-44 h-auto shrink-0 rounded-xl safari-overflow-hidden-fix overflow-hidden"
          />
        </div>

        <div
          :class="[{ 'text-white': backgroundColor === 'magenta' }]"
          class="flex w-full flex-1 flex-col gap-4 xl:gap-6"
        >
          <SvgIcon
            v-if="showQuote"
            :class="[{ 'text-white': backgroundColor === 'magenta' }]"
            class="h-10 w-10 text-dark-blue xl:mb-2 xl:h-16 xl:w-16"
            name="icon-quote"
          />
          <div>
            <Heading
              v-if="headline.text"
              :size="headingSizeMapping[fontSize]"
              :tag="headline?.tag ?? 'h2'"
            >
              {{ headline.text }}
            </Heading>
          </div>
          <div>{{ subline }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
